import MapboxDraw from 'twc-mapbox-gl-draw/dist/mapbox-gl-draw';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import DrawPolygonByPoints from './mode';

import drawingStyles from './styles';
import { newPropertyFilter } from '../../request-filters';
import { applyFeatureStates, copyAllFeatureState, setFeatureStateForSources } from '../../features';
import { locationSources } from '../../layers';
import { fitBoundsAsync, getBoundingBoxFromFlatCoords, newBoundingBoxEvent } from '../../bounding-box';
import { addAreaToMap } from '../../area';
import { sanitiseCoordinates } from '../../spatial';
import { fadeIn, fadeOut } from '../../../animations/fade';
import { upsertHiddenInput } from '../../../dom/create';
import { captureInputState } from '../../../forms/input';
import Logger from '../../../logging/logger';

const modes = Object.assign( {}, MapboxDraw.modes, {
    static: StaticMode,
    // eslint-disable-next-line camelcase
    draw_polygon_by_points: DrawPolygonByPoints,
} );

const notificationID = 'draw-your-search-instructions';

class DrawYourSearch {
    constructor( mapSearch, options = {} ) {
        this.options = Object.assign( {}, DrawYourSearch.defaultOptions, options );
        this.mapSearch = mapSearch;
        this.searchURL = this.options.searchURL;
        this.active = false;
        this.stateBeforeActivation = {};

        this.draw = new MapboxDraw( {
            styles: drawingStyles,
            displayControlsDefault: false,
            modes: modes,
            keybindings: true,
            boxSelect: false,
        } );
    }

    init( map ) {
        const controlContainer = document.querySelector( '.twc__map-search-control-container-bottom' );

        if ( !controlContainer ) {
            return;
        }

        this.map = map;

        document.documentElement.classList.add( `mapsearch__${DrawYourSearch.id}` );

        this.container = document.createElement( 'div' );
        this.container.className = 'drawyoursearch__container drawyoursearch__container--withneighbour';

        this.initiateButton = document.createElement( 'button' );
        this.initiateButton.type = 'button';
        this.initiateButton.style.opacity = '1';
        this.initiateButton.className = 'twc__map-search-item twc__map-drawyoursearch--btn-initiate';
        this.initiateButton.innerHTML = this.mapSearch.getTemplateString( 'btnStartDrawYourSearch' );
        this.initiateButton.title = this.mapSearch.getTemplateString( 'btnStartDrawYourSearch' );
        this.initiateButton.addEventListener( 'click', this.activate.bind( this ) );

        this.container.appendChild( this.initiateButton );

        this.cancelButton = document.createElement( 'button' );
        this.cancelButton.type = 'button';
        this.cancelButton.style.opacity = '0';
        this.cancelButton.style.display = 'none';
        this.cancelButton.className = 'twc__map-search-item twc__map-drawyoursearch--btn-quit';
        this.cancelButton.innerHTML = `<img src="${window.assetsPrefix + '/images/icons/close-fa.svg'}" width="13" height="16" loading="lazy" />${this.mapSearch.getTemplateString( 'btnQuitDrawYourSearch' )}`;
        this.cancelButton.title = this.mapSearch.getTemplateString( 'btnQuitDrawYourSearch' );
        this.cancelButton.addEventListener( 'click', this.onCancel.bind( this ) );

        this.container.appendChild( this.cancelButton );

        window.addEventListener( 'twc:mapsearch:draw-your-search:deactivate', this.handleDeactivate.bind( this ) );

        window.addEventListener( 'twc:mapsearch:draw-your-search:activate', this.activate.bind( this ) );

        window.addEventListener( 'twc:mapsearch:draw-your-search:remove-features', this.draw.deleteAll );

        this.map.on( 'draw.create', this.onDrawingCreated.bind( this ) );

        this.map.on( 'draw.interrupt', this.onInterrupt.bind( this ) );

        this.map.on( 'draw.mousemove', this.onMouseMove.bind( this ) );

        this.map.addControl( this.draw );

        controlContainer.appendChild( this.container );
    }

    async activate() {
        Logger.info( `[map] Activating ${DrawYourSearch.id}` );

        if ( document.body.classList.contains( 'popup-open-search' ) ) {
            this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:search-popup-close', { bubbles: true } ) );
        }

        this.map.getCanvas().style.cursor = 'crosshair';

        this.initiateButton.disabled = true;

        fadeOut( this.initiateButton );
        this.initiateButton.style.display = 'none';

        this.cancelButton.style.display = '';
        await fadeIn( this.cancelButton );

        this.container.classList.remove( 'drawyoursearch__container--withneighbour' );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

        if ( this.map.getLayer( 'properties' ) ) {
            this.map.setLayoutProperty( 'properties', 'visibility', 'none', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'area-close:fill' ) ) {
            this.map.setLayoutProperty( 'area-close:fill', 'visibility', 'none', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'area:fill' ) ) {
            this.map.setLayoutProperty( 'area:fill', 'visibility', 'none', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'radius-close:fill' ) ) {
            this.map.setLayoutProperty( 'radius-close:fill', 'visibility', 'none', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'radius:fill' ) ) {
            this.map.setLayoutProperty( 'radius:fill', 'visibility', 'none', this.mapSearch.debug );
        }

        this.stateBeforeActivation.featureState = copyAllFeatureState( this.map );

        setFeatureStateForSources( this.map, locationSources, {
            active: false,
        } );

        const locationFreeText = document.querySelector( '#property-search-form-base input[name=locationFreeText]' );
        const locationID = document.querySelector( '#property-search-form-base input[name=locationId]' );

        if ( locationFreeText && locationID && locationID.value !== DrawYourSearch.id ) {
            this.stateBeforeActivation = {
                ...this.stateBeforeActivation,
                locationID: captureInputState( locationID ),
                locationFreeText: captureInputState( locationFreeText ),
            };
        }

        this.draw.deleteAll();

        this.draw.changeMode( 'draw_polygon_by_points' );

        this.active = true;

        this.container.dispatchEvent( new CustomEvent( 'twc:mapsearch:toggle-component', {
            bubbles: true,
            detail: {
                active: this.active,
                componentID: DrawYourSearch.id,
            },
        } ) );

        const hasSeenNotificationInSession = window.appStorage.getSessionItem( `mapsearch.notification.${notificationID}.hide` ) === 'true';

        if ( !hasSeenNotificationInSession ) {
            this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-notification', {
                bubbles: true,
                detail: {
                    id: notificationID,
                    msg: this.mapSearch.getTemplateString( 'drawYourSearchNotification' ),
                    options: {
                        autohide: false,
                        btn: ( handler ) => {
                            const hideBtn = document.createElement( 'button' );
                            hideBtn.className = 'twc__map-search-item twc__map-search-item--dark mt-sm';
                            hideBtn.type = 'button';
                            hideBtn.innerHTML = this.mapSearch.getTemplateString( 'drawYourSearchNotificationClose' );
                            hideBtn.dataset.forNotification = notificationID;

                            const close = () => {
                                window.appStorage.setSessionItem( `mapsearch.notification.${notificationID}.hide`, 'true' );
                                handler.hide();
                            };

                            hideBtn.addEventListener( 'click', close );

                            handler.map.once( 'draw.click', close );

                            return hideBtn;
                        },
                    },
                },
            } ) );
        }
    }

    async handleDeactivate( event ) {
        await this.deactivate( event.detail || {} );
    }

    async deactivate( options = {} ) {
        options = Object.assign( {}, DrawYourSearch.defaultDeactivationOptions, options );

        Logger.info( `[map] Deactivating ${DrawYourSearch.id}`, options );

        this.container.classList.add( 'drawyoursearch__container--withneighbour' );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:hide-notification', {
            bubbles: true,
            detail: {
                notificationID: notificationID,
            },
        } ) );

        if ( this.active ) {
            this.active = false;

            await fadeOut( this.cancelButton );
            this.cancelButton.style.display = 'none';

            this.initiateButton.disabled = false;
            this.initiateButton.style.display = '';
            await fadeIn( this.initiateButton );

            applyFeatureStates( this.map, this.stateBeforeActivation.featureState );

            this.container.dispatchEvent( new CustomEvent( 'twc:mapsearch:toggle-component', {
                bubbles: true,
                detail: {
                    active: this.active,
                    componentID: DrawYourSearch.id,
                },
            } ) );
        }

        this.draw.deleteAll();

        if ( this.draw.getMode() !== 'static' ) {
            this.draw.changeMode( 'static' );
        }

        if ( this.map.getLayer( 'properties' ) ) {
            this.map.setLayoutProperty( 'properties', 'visibility', 'visible', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'area-close:fill' ) ) {
            this.map.setLayoutProperty( 'area-close:fill', 'visibility', 'visible', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'area:fill' ) ) {
            this.map.setLayoutProperty( 'area:fill', 'visibility', 'visible', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'radius-close:fill' ) ) {
            this.map.setLayoutProperty( 'radius-close:fill', 'visibility', 'visible', this.mapSearch.debug );
        }

        if ( this.map.getLayer( 'radius:fill' ) ) {
            this.map.setLayoutProperty( 'radius:fill', 'visibility', 'visible', this.mapSearch.debug );
        }

        if ( options.search ) {
            this.mapSearch.baseForm.dispatchEvent( new CustomEvent( 'twc:mapsearch:form-submit', {
                bubbles: true,
                detail: {
                    addArea: false,
                    focusMapToSearch: false,
                    resetLayers: false,
                    triggerStateChange: options.triggerStateChange,
                },
            } ) );
        }
    }

    async onDrawingCreated( event ) {
        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', { bubbles: true } ) );

        fadeOut( this.cancelButton );
        this.cancelButton.style.display = 'none';

        this.initiateButton.disabled = false;
        this.initiateButton.style.display = '';
        await fadeIn( this.initiateButton );

        this.container.classList.add( 'drawyoursearch__container--withneighbour' );

        const saimContainer = document.querySelector( '.searchthisarea__container' );

        if ( saimContainer ) {
            saimContainer.style.display = 'none';
        }

        this.options.eventHandlersToDisable.forEach( ( event ) => {
            this.map.off( event.name, event.handler );
        } );

        const drawnFeatures = event.features;

        Logger.info( '[map] Polygon Created', event );

        if ( drawnFeatures.length === 0 ) {
            return;
        }

        this.mapSearch.setLoading( true );

        this.mapSearch.currentLocation = null;

        const feature = drawnFeatures[ 0 ];

        const coordinates = sanitiseCoordinates( feature.geometry.coordinates[ 0 ] );

        upsertHiddenInput( this.mapSearch.popupForm, 'area', coordinates.toString() );
        upsertHiddenInput( this.mapSearch.baseForm, 'area', coordinates.toString() );

        if ( this.map.getLayer( 'properties' ) ) {
            this.map.setLayoutProperty( 'properties', 'visibility', 'visible', this.mapSearch.debug );
        }

        this.draw.deleteAll();

        const areaFeature = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: coordinates,
            },
        };

        const areaOptions = {
            addClose: true,
            closeEventDetail: {},
        };

        if ( this.stateBeforeActivation.locationID &&
            this.stateBeforeActivation.locationFreeText
        ) {
            areaOptions.closeEventDetail = {
                locationID: this.stateBeforeActivation.locationID,
                locationFreeText: this.stateBeforeActivation.locationFreeText,
            };
        }

        addAreaToMap( this.mapSearch, areaFeature, areaOptions );

        if ( this.map.getLayer( 'properties' ) ) {
            this.map.moveLayer( 'area:fill', 'properties' );
        }

        const bbox = getBoundingBoxFromFlatCoords( feature.geometry.coordinates[ 0 ] );

        const areaBboxOptions = {
            padding: {
                top: 50,
                right: 50,
                bottom: 50,
                left: 50,
            },
        };

        const bboxEvent = newBoundingBoxEvent();

        await fitBoundsAsync( this.mapSearch, {
            bbox: bbox,
            options: areaBboxOptions,
            event: bboxEvent,
        } );

        setFeatureStateForSources( this.map, locationSources, {
            active: false,
        } );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:typeahead:set-value', {
            bubbles: true,
            detail: {
                label: this.mapSearch.getTemplateString( 'searchThisAreaTypeaheadLabel' ),
                state: {
                    value: DrawYourSearch.id,
                },
            },
        } ) );

        this.active = false;

        this.container.dispatchEvent( new CustomEvent( 'twc:mapsearch:toggle-component', {
            bubbles: true,
            detail: {
                active: this.active,
                componentID: DrawYourSearch.id,
            },
        } ) );

        await this.search( feature );

        this.mapSearch.setLoading( false );
    }

    async search() {
        const propertyParams = newPropertyFilter( {}, this.mapSearch.baseForm );

        const parametersToRemove = [
            'currentLocationID',
            'currentLocationType',
            'locationBoundSW',
            'locationBoundNE',
            'latitude',
            'longitude',
            'radius',
        ];

        parametersToRemove.forEach( ( param ) => propertyParams.delete( param ) );

        const options = {
            fetchFeatures: false,
            fetchProperties: true,
            propertySearchURL: this.searchURL,
            propertySearchParams: propertyParams,
        };

        const meta = {
            addArea: false,
            addAreaClose: false,
            triggerStateChange: true,
        };

        await this.mapSearch.update( options, meta );

        this.mapSearch.previousSearchState = propertyParams.toString();
    }

    async onCancel() {
        await this.deactivate( {
            search: false,
            triggerStateChange: false,
        } );
    }

    async onInterrupt() {
        this.deactivate( {
            search: false,
            triggerStateChange: false,
        } );
    }

    onMouseMove( event ) {
        const { isDrawnPoint, isFirstDrawnPoint } = event;

        const firstPointClass = 'mode-draw_polygon_by_points--firstpoint';

        if ( isFirstDrawnPoint ) {
            this.mapSearch.root.classList.add( firstPointClass );
        } else {
            this.mapSearch.root.classList.remove( firstPointClass );
        }

        const pointClass = 'mode-draw_polygon_by_points--point';

        if ( isDrawnPoint ) {
            this.mapSearch.root.classList.add( pointClass );
        } else {
            this.mapSearch.root.classList.remove( pointClass );
        }
    }
}

DrawYourSearch.id = 'draw-your-search';

DrawYourSearch.defaultDeactivationOptions = {
    search: true,
    triggerStateChange: true,
};

DrawYourSearch.defaultOptions = {
    searchURL: null,
    eventHandlersToDisable: [],
};

export default DrawYourSearch;
